import React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { Modal } from "@clarkinc/zest-design-system";

interface AB1200WarningProps {
  ab1200Chemicals?: string[];
  spanishAb1200Chemicals?: string[];
}

interface DisclosureLinkProps {
  children: string;
  style?: React.CSSProperties;
}

interface WarningIconProps {
  style?: React.CSSProperties;
}

const WarningIcon = ({ style }: WarningIconProps) => (
  <img
    alt=""
    className="details-disclaimer-icon -mb-0.5 mr-1"
    src={`${
      window?.location?.origin || ""
    }/uploads/design/2018/7/prop-65-warning-icon.png`}
    style={style}
  />
);

const DisclosureLink = ({ children, style }: DisclosureLinkProps) => (
  <a
    href="https://www.clarkassociatesinc.biz/cookware-chemical-disclosure"
    rel="noopener noreferrer"
    target="_blank"
    className="ml-1"
    style={style}
  >
    {children}
  </a>
);

const enFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

const esFormatter = new Intl.ListFormat("es", {
  style: "long",
  type: "conjunction",
});

const ModalContentEnglish = ({ ab1200Chemicals }: AB1200WarningProps) => {
  return (
    <p className="leading-6 mb-3 text-left text-lg">
      {ab1200Chemicals?.length ? (
        <span>
          <WarningIcon />
          <span className="font-bold">WARNING:</span>{" "}
          <span data-testid="modal-english-with-chemicals">
            This product contains chemical substances [
            {enFormatter.format(ab1200Chemicals)}] relevant to CA AB1200. For
            more information about the chemicals in this product, visit
          </span>
          <DisclosureLink>
            Clark Associates&apos; cookware disclosure page
          </DisclosureLink>
          .
        </span>
      ) : (
        <span>
          <WarningIcon />
          <span className="font-bold">WARNING:</span> This product contains
          chemical substances relevant to CA AB1200. For more information about
          chemicals in this product, visit
          <DisclosureLink>
            Clark Associates&apos; cookware disclosure page
          </DisclosureLink>
          .
        </span>
      )}
    </p>
  );
};

const ModalContentSpanish = ({
  spanishAb1200Chemicals,
}: AB1200WarningProps) => {
  return (
    <p className="leading-6 mb-0 text-left text-lg">
      {spanishAb1200Chemicals?.length ? (
        <span>
          <WarningIcon />
          <span className="font-bold">ADVERTENCIA:</span> Este producto contiene
          sustancias químicas [{esFormatter.format(spanishAb1200Chemicals)}]
          relevantes para CA AB1200. Para obtener más información sobre las
          sustancias químicas de este producto, visite
          <DisclosureLink>
            la página de divulgación de utensilios de cocina de Clark Associates
          </DisclosureLink>
          .
        </span>
      ) : (
        <span>
          <WarningIcon />
          <span className="font-bold">ADVERTENCIA:</span> Este producto contiene
          sustancias químicas relevantes para CA AB1200. Para obtener más
          información sobre las sustancias químicas de este producto, visite
          <DisclosureLink>
            la página de divulgación de utensilios de cocina de Clark Associates
          </DisclosureLink>
          .
        </span>
      )}
    </p>
  );
};

const PrintContentEnglish = ({ ab1200Chemicals }: AB1200WarningProps) => {
  return (
    <p
      style={{
        lineHeight: "24px",
        marginBottom: "12px",
        marginTop: "0",
      }}
    >
      {ab1200Chemicals?.length ? (
        <span>
          <WarningIcon
            style={{
              height: "19px",
              marginBottom: "-2px",
              marginRight: "4px",
              width: "19px",
            }}
          />
          <span style={{ fontFamily: "sans-serif", fontWeight: "700" }}>
            WARNING:
          </span>{" "}
          This product contains chemical substances [
          {enFormatter.format(ab1200Chemicals)}] relevant to CA AB1200. For more
          information about the chemicals in this product, visit
          <DisclosureLink
            style={{
              color: "#000",
              fontWeight: "900",
              marginLeft: "4px",
              marginBottom: "0",
              marginTop: "0",
              textDecoration: "none",
            }}
          >
            Clark Associates&apos; AB1200 disclosure page
          </DisclosureLink>
          .
        </span>
      ) : (
        <span>
          <WarningIcon
            style={{
              height: "19px",
              marginBottom: "-2px",
              marginRight: "4px",
              width: "19px",
            }}
          />
          <span style={{ fontFamily: "sans-serif", fontWeight: "700" }}>
            WARNING:
          </span>{" "}
          This product contains chemical substances relevant to CA AB1200. For
          more information about chemicals in this product, visit
          <DisclosureLink
            style={{
              color: "#000",
              fontWeight: "900",
              marginLeft: "4px",
              marginBottom: "0",
              marginTop: "0",
              textDecoration: "none",
            }}
          >
            Clark Associates&apos; AB1200 disclosure page
          </DisclosureLink>
          .
        </span>
      )}
    </p>
  );
};

const PrintContentSpanish = ({
  spanishAb1200Chemicals,
}: AB1200WarningProps) => {
  return (
    <p
      style={{
        lineHeight: "24px",
        margin: "0",
      }}
    >
      {spanishAb1200Chemicals?.length ? (
        <span>
          <WarningIcon
            style={{
              height: "19px",
              marginBottom: "-2px",
              marginRight: "4px",
              width: "19px",
            }}
          />
          <span style={{ fontWeight: "700" }}>ADVERTENCIA:</span> Este producto
          contiene sustancias químicas [
          {esFormatter.format(spanishAb1200Chemicals)}] relevantes para CA AB
          1200. Para obtener más información sobre las sustancias químicas de
          este producto, visite
          <DisclosureLink
            style={{
              color: "#000",
              fontWeight: "900",
              margin: "0 0 0 4px",
              textDecoration: "none",
            }}
          >
            Clark Associates&apos; AB1200 página de divulgación
          </DisclosureLink>
          .
        </span>
      ) : (
        <span>
          <WarningIcon
            style={{
              height: "19px",
              marginBottom: "-2px",
              marginRight: "4px",
              width: "19px",
            }}
          />
          <span style={{ fontWeight: "700" }}>ADVERTENCIA:</span> Este producto
          contiene sustancias químicas relevantes para CA AB1200. Para obtener
          más información sobre las sustancias químicas de este producto, visite
          <DisclosureLink
            style={{
              color: "#000",
              fontWeight: "900",
              margin: "0 0 0 4px",
              textDecoration: "none",
            }}
          >
            Clark Associates&apos; AB1200 página de divulgación
          </DisclosureLink>
          .
        </span>
      )}
    </p>
  );
};

const WarningContent = ({
  ab1200Chemicals,
  spanishAb1200Chemicals,
}: AB1200WarningProps): JSX.Element => (
  <div
    style={{
      backgroundColor: "#fff",
      border: "2px solid",
      borderRadius: "8px",
      color: "#000",
      fontFamily: "sans-serif",
      fontSize: "18px",
      margin: "0 auto",
      maxWidth: "100%",
      padding: "10px",
      textAlign: "left",
      width: "430px",
    }}
  >
    <PrintContentEnglish ab1200Chemicals={ab1200Chemicals} />
    <PrintContentSpanish spanishAb1200Chemicals={spanishAb1200Chemicals} />
  </div>
);

const AB1200Warning = ({
  ab1200Chemicals,
  spanishAb1200Chemicals,
}: AB1200WarningProps) => {
  function onClickPrint(): void {
    const printWindow = window.open("", "_blank", "width=600,height=600");

    if (!printWindow) return;
    printWindow.document.title = "AB1200 Warning";
    const root = createRoot(printWindow.document.body);
    flushSync(() =>
      root.render(
        <WarningContent
          ab1200Chemicals={ab1200Chemicals}
          spanishAb1200Chemicals={spanishAb1200Chemicals}
        />,
      ),
    );
    printWindow.print();
  }

  return (
    <>
      <Modal.Trigger
        className="bg-transparent font-bold text-base leading-6 p-0 text-green-500 active:text-green-800 active:underline hover:text-green-700 hover:underline"
        htmlFor="AB1200-warning-modal"
        type="button"
        theme="headless"
      >
        AB1200 Warning
      </Modal.Trigger>
      <Modal
        aria-label="AB1200 Warning"
        headerTitle="Attention CA Residents"
        footerProps={{
          primaryButtonProps: {
            onClick: onClickPrint,
            type: "button",
          },
        }}
        id="AB1200-warning-modal"
        primaryButtonText="Print this Message"
        theme="wss"
      >
        <ModalContentEnglish ab1200Chemicals={ab1200Chemicals} />
        <ModalContentSpanish spanishAb1200Chemicals={spanishAb1200Chemicals} />
      </Modal>
    </>
  );
};

export default AB1200Warning;
